"use client";

import type { MutableRefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

export type UseElementInViewOptions = {
  rootMargin?: string;
  threshold?: number | number[];
  freezeOnceVisible?: boolean;
  exitDebounceMs?: number;
  resolveRoot?: () => Element | null;
};

export type UseElementInViewResult<T extends HTMLElement> = {
  isInView: boolean;
  targetRef: (node: T | null) => void;
  hasBeenInViewRef: MutableRefObject<boolean>;
};

/**
 * 任意要素の表示状態を監視し、遅延描画の可否を返す汎用フック。
 *
 * 目的:
 * - IntersectionObserver を使った遅延描画判定を複数画面で再利用する
 * - 初回表示時の近傍判定でチラつきを抑え、画面体験を安定化する
 *
 * 契約:
 * - `freezeOnceVisible=true` の場合、一度表示されたら監視を停止して true を維持する
 * - `exitDebounceMs` を指定した場合、非表示遷移は短時間だけ遅延させて境界揺れを吸収する
 * - `resolveRoot` を省略した場合は viewport を root として監視する
 *
 * 失敗時挙動:
 * - IntersectionObserver 非対応環境では fail-open として常に表示可能 (`isInView=true`) にする
 */
export function useElementInView<T extends HTMLElement = HTMLDivElement>({
  rootMargin = "240px 0px",
  threshold = 0.1,
  freezeOnceVisible = false,
  exitDebounceMs = 0,
  resolveRoot,
}: UseElementInViewOptions = {}): UseElementInViewResult<T> {
  const [target, setTarget] = useState<T | null>(null);
  const targetRef = useCallback((node: T | null) => {
    setTarget(node);
  }, []);
  const [isInView, setIsInView] = useState(false);
  const hasBeenInViewRef = useRef(false);
  const exitTimeoutRef = useRef<number | null>(null);

  const clearPendingExit = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (exitTimeoutRef.current === null) {
      return;
    }
    window.clearTimeout(exitTimeoutRef.current);
    exitTimeoutRef.current = null;
  }, []);

  const commitVisibility = useCallback((nextVisibility: boolean) => {
    setIsInView((currentVisibility) =>
      currentVisibility === nextVisibility ? currentVisibility : nextVisibility,
    );
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!target) {
      clearPendingExit();
      return;
    }

    if (!("IntersectionObserver" in window)) {
      clearPendingExit();
      commitVisibility(true);
      return;
    }

    const setVisible = () => {
      clearPendingExit();
      hasBeenInViewRef.current = true;
      commitVisibility(true);
    };

    const setHidden = () => {
      if (freezeOnceVisible) {
        return;
      }
      clearPendingExit();
      if (exitDebounceMs <= 0) {
        commitVisibility(false);
        return;
      }
      // 交差境界の短い出入りでは即アンマウントせず、scroll 中の揺れを吸収する。
      exitTimeoutRef.current = window.setTimeout(() => {
        exitTimeoutRef.current = null;
        commitVisibility(false);
      }, exitDebounceMs);
    };

    const root = resolveRoot?.() ?? null;
    const rootRect = root?.getBoundingClientRect() ?? {
      top: 0,
      bottom: window.innerHeight,
    };
    const targetRect = target.getBoundingClientRect();
    const marginValues = rootMargin.split(" ").filter((value) => value.length);
    const parseMargin = (value?: string) =>
      Number.parseFloat(value ?? "0") || 0;
    const marginTop = parseMargin(marginValues[0]);
    const marginBottom = parseMargin(marginValues[2] ?? marginValues[0] ?? "0");
    const isInitiallyVisible =
      targetRect.bottom >= rootRect.top - marginTop &&
      targetRect.top <= rootRect.bottom + marginBottom;

    if (isInitiallyVisible) {
      setVisible();
      if (freezeOnceVisible) {
        return;
      }
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        if (entry.isIntersecting) {
          setVisible();
          if (freezeOnceVisible) {
            observer.disconnect();
          }
        } else {
          setHidden();
        }
      },
      {
        root,
        rootMargin,
        threshold,
      },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
      clearPendingExit();
    };
  }, [
    clearPendingExit,
    commitVisibility,
    exitDebounceMs,
    freezeOnceVisible,
    resolveRoot,
    rootMargin,
    target,
    threshold,
  ]);

  return { isInView, targetRef, hasBeenInViewRef };
}
